import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError, CollectionRow, EventDetail } from '../../api';
import { eventEndpoints } from '../../api';
import { usePermissions } from '../../session';
import { Button, ErrorNote, Panel } from '../../ui';
import { INPUT } from './shared';

/**
 * The series and the season this event belongs to (D-169).
 *
 * Both columns have existed since the first migration and there has never been
 * a way to put anything in them, so every event in the system has had both
 * null.
 *
 * They are shown together and described apart, because the temptation is to
 * treat them as one thing:
 *
 *   * a **series** is a marketing strand — a name the venue promotes under,
 *     with no dates of its own;
 *   * a **season** is dated, and it is what a package renewal is against.
 *
 * Both can be created from here, for the same reason a category can (D-155):
 * the alternative is a migration every time the venue books a new strand.
 */
export function CollectionsPanel({ detail, onError }: { detail: EventDetail; onError: (error: ApiError) => void }) {
  const can = usePermissions();
  const queryClient = useQueryClient();
  const editable = can('events.manage');
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState<'series' | 'season' | null>(null);
  const [name, setName] = useState('');
  const [startsOn, setStartsOn] = useState('');
  const [endsOn, setEndsOn] = useState('');

  const series = useQuery({ queryKey: ['series'], queryFn: () => eventEndpoints.series() });
  const seasons = useQuery({ queryKey: ['seasons', 'collections'], queryFn: () => eventEndpoints.seasons() });
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['event', detail.event.id] });
    void queryClient.invalidateQueries({ queryKey: ['series'] });
    void queryClient.invalidateQueries({ queryKey: ['seasons'] });
  };

  const assign = useMutation({
    mutationFn: (input: Record<string, unknown>) => eventEndpoints.update(detail.event.id, input),
    onSuccess: refresh,
    onError,
  });

  const createSeries = useMutation({
    mutationFn: () => eventEndpoints.createSeries({ name: name.trim() }),
    onSuccess: (result) => {
      close();
      assign.mutate({ series_id: result.series.id });
    },
    onError: (cause: ApiError) => setError(cause.message),
  });

  const createSeason = useMutation({
    mutationFn: () => eventEndpoints.createSeason({ name: name.trim(), starts_on: startsOn, ends_on: endsOn }),
    onSuccess: (result) => {
      close();
      assign.mutate({ season_id: result.season.id });
    },
    onError: (cause: ApiError) => setError(cause.message),
  });

  function close() {
    setAdding(null);
    setName('');
    setStartsOn('');
    setEndsOn('');
    setError(null);
  }

  const label = (row: CollectionRow) => `${row.name}${row.events ? ` · ${row.events} event${row.events === 1 ? '' : 's'}` : ''}`;

  return (
    <Panel title="Series and season" description="A series is a strand you promote under; a season is dated, and it is what a package renewal is against. Both are optional.">
      <div className="space-y-4 px-5 py-4">
        {error ? <ErrorNote>{error}</ErrorNote> : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="text-sm" htmlFor="event-series">
              <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">Series</span>
              <select
                id="event-series"
                className={`mt-1 w-full ${INPUT}`}
                disabled={!editable || assign.isPending}
                value={detail.event.series_id ?? ''}
                onChange={(event) => assign.mutate({ series_id: event.target.value || null })}
              >
                <option value="">None</option>
                {(series.data?.series ?? []).map((row) => (
                  <option key={row.id} value={row.id}>
                    {label(row)}
                  </option>
                ))}
              </select>
            </label>
            {editable ? (
              <button type="button" className="mt-1 text-xs text-accent underline-offset-2 hover:underline" onClick={() => setAdding('series')}>
                New series
              </button>
            ) : null}
            {detail.event.series_id ? (
              <p className="mt-1 text-xs text-ink-400">
                Customers see it at /series/{(series.data?.series ?? []).find((row) => row.id === detail.event.series_id)?.slug ?? '…'}
              </p>
            ) : null}
          </div>

          <div>
            <label className="text-sm" htmlFor="event-season">
              <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">Season</span>
              <select
                id="event-season"
                className={`mt-1 w-full ${INPUT}`}
                disabled={!editable || assign.isPending}
                value={detail.event.season_id ?? ''}
                onChange={(event) => assign.mutate({ season_id: event.target.value || null })}
              >
                <option value="">None</option>
                {(seasons.data?.seasons ?? []).map((row) => (
                  <option key={row.id} value={row.id}>
                    {label(row)}
                  </option>
                ))}
              </select>
            </label>
            {editable ? (
              <button type="button" className="mt-1 text-xs text-accent underline-offset-2 hover:underline" onClick={() => setAdding('season')}>
                New season
              </button>
            ) : null}
          </div>
        </div>

        {adding ? (
          <form
            className="flex flex-wrap items-end gap-3 border border-line bg-page p-3"
            onSubmit={(event) => {
              event.preventDefault();
              if (adding === 'series') createSeries.mutate();
              else createSeason.mutate();
            }}
          >
            <label className="text-sm">
              <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">Name</span>
              <input value={name} onChange={(event) => setName(event.target.value)} placeholder={adding === 'series' ? 'Comedy at the Auditorium' : '2027 Season'} className={`mt-1 w-64 ${INPUT}`} autoFocus />
            </label>
            {adding === 'season' ? (
              <>
                <label className="text-sm">
                  <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">Starts</span>
                  <input type="date" value={startsOn} onChange={(event) => setStartsOn(event.target.value)} className={`mt-1 ${INPUT}`} />
                </label>
                <label className="text-sm">
                  <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">Ends</span>
                  <input type="date" value={endsOn} onChange={(event) => setEndsOn(event.target.value)} className={`mt-1 ${INPUT}`} />
                </label>
              </>
            ) : null}
            <Button type="submit" variant="primary" disabled={!name.trim() || (adding === 'season' && (!startsOn || !endsOn)) || createSeries.isPending || createSeason.isPending}>
              Create and apply
            </Button>
            <Button type="button" onClick={close}>
              Cancel
            </Button>
            <p className="text-xs text-ink-400">The web address is made from the name.</p>
          </form>
        ) : null}
      </div>
    </Panel>
  );
}
